import { ArrowsClockwiseIcon, CaretUpDownIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { Button, Space, Table, type TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { clsx } from 'clsx';
import { type Key, type MouseEvent, useEffect, useMemo, useState } from 'react';

import { RegionError } from '@/components/feedback/error-state';

import styles from './data-table.module.scss';
import { PaginationFooter } from './pagination-footer';
import { TableSkeleton } from './table-skeleton';
import { ACTION_COLUMN_WIDTH, type DataTableProps, FLOATING_ACTION_COLUMN_WIDTH } from './types';
import { useTableState } from './use-table-state';

function withSortIcon<TRow>(columns: ColumnsType<TRow>): ColumnsType<TRow> {
  return columns.map((column) =>
    'sorter' in column && column.sorter && !column.sortIcon
      ? {
          ...column,
          sortIcon: ({ sortOrder }: { sortOrder: 'ascend' | 'descend' | null }) => (
            <CaretUpDownIcon
              size={14}
              aria-hidden
              className={sortOrder ? styles.sortIconActive : styles.sortIcon}
            />
          ),
        }
      : column,
  );
}

export function DataTable<
  TRow extends object,
  TFilters extends Record<string, unknown> = Record<string, unknown>,
>({
  tableId,
  columns,
  request,
  rowKey,
  defaultPageSize = 25,
  pageSizeOptions,
  hidePagination = false,
  keepPageOnSort = false,
  errorText,
  loadErrorText,
  skeletonHeaders = false,
  refetchInterval,
  emptyText,
  hideReload = false,
  filters,
  toolbar,
  bulkActions,
  actionColumn,
  onRowClick,
  isRowClickable,
  rowClassName,
  stateStore,
  className,
  onDataChange,
}: DataTableProps<TRow, TFilters>) {
  // Always call the URL hook (keeps hook order stable); a provided stateStore overrides it.
  const urlState = useTableState(tableId, defaultPageSize);
  const { page, pageSize, sort, setState } = stateStore ?? urlState;
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<TRow[]>([]);

  const query = useQuery({
    queryKey: [tableId, { page, pageSize, sort, filters }, request],
    queryFn: () => request({ page, pageSize, sort, filters: filters ?? ({} as TFilters) }),
    placeholderData: (prev) => prev,
    refetchInterval,
  });

  useEffect(() => {
    if (query.data) onDataChange?.(query.data.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onDataChange is not stable across renders
  }, [query.data]);

  const themedColumns = useMemo(() => {
    const base = withSortIcon(columns);
    if (!actionColumn) return base;
    // A hover-revealed ellipsis floats over the row's right edge, Gmail-style: its column stays
    // sticky, so the kebab sits at the visible right edge however far the table is scrolled, but
    // reserves no frozen strip of the viewport. An always-visible cell (the Dashboard's
    // Approve/Reject pair) is the surface's point, so it keeps a real frozen column.
    const floating = actionColumn.revealOnHover !== false;
    const width = floating
      ? FLOATING_ACTION_COLUMN_WIDTH
      : (actionColumn.width ?? ACTION_COLUMN_WIDTH);
    return [
      ...base,
      {
        key: '__actions',
        title: '',
        fixed: 'right' as const,
        width,
        // The default width only fits the kebab once antd's 20px cell padding is tightened; a
        // caller sizing its own column (the Dashboard's Approve/Reject pair) keeps the padding.
        className: floating
          ? styles.floatingActionCell
          : actionColumn.width == null
            ? styles.actionCellCompact
            : undefined,
        render: (_: unknown, row: TRow) => (
          // Separate hooks, deliberately: `data-dt-action-cell` is always present (the row-click
          // handler below reads it to ignore clicks on the actions), while
          // `data-dt-reveal-on-hover` / `data-dt-floating-action` carry the styling. Data
          // attributes rather than CSS-module classes because `css: false` in vitest stubs the
          // module, so a class assertion would test the stub instead of the DOM.
          <div
            data-dt-action-cell
            {...(floating ? { 'data-dt-reveal-on-hover': '', 'data-dt-floating-action': '' } : {})}
          >
            {actionColumn.render(row)}
          </div>
        ),
      },
    ];
  }, [columns, actionColumn]);

  // Fixed (sticky) action columns require antd's own scroll container; other tables scroll
  // horizontally via the `.tableCard` overflow so a single table keeps header+body aligned.
  const hasFixedColumn = themedColumns.some((col) => 'fixed' in col && col.fixed);

  // table-layout:fixed only kicks in once a page opts in with real widths (ADR-0032) — otherwise
  // antd's default auto layout keeps sizing columns to content, today's behaviour for every
  // existing caller that hasn't adopted widths yet.
  const hasExplicitWidth = columns.some((col) => 'width' in col && col.width != null);

  // `scroll.x: 'max-content'` asks the browser to measure the table's intrinsic content width —
  // which, empirically, still reflects a long cell's real (unbroken) text width even under
  // `table-layout: fixed`, rather than strictly the declared column widths (verified via computed
  // style: 3022px rendered vs. ~2026px of declared widths, ADO 113388). Once every column
  // (including the action column) declares a width, sum them and hand antd that exact number
  // instead, so the table's width is never content-dependent.
  const allColumnsHaveWidth = themedColumns.every((col) => 'width' in col && col.width != null);
  const totalWidth = allColumnsHaveWidth
    ? themedColumns.reduce((sum, col) => sum + (col.width as number), 0)
    : undefined;

  if (import.meta.env.DEV && actionColumn && !hasExplicitWidth) {
    console.warn(
      `[DataTable "${tableId}"] no column declares a width — one long cell can still blow out ` +
        'this table (ADO 113388). Add a `width` (from Figma) to at least one column.',
    );
  }

  const onChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<TRow> | SorterResult<TRow>[],
  ) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    setState({
      page: keepPageOnSort ? page : 1,
      sort:
        single?.field && single.order
          ? { field: String(single.field), order: single.order }
          : undefined,
    });
  };

  const clearSelection = () => {
    setSelectedKeys([]);
    setSelectedRows([]);
  };

  if (query.isLoading && !query.data) {
    return (
      <TableSkeleton
        columns={columns.length}
        headers={
          skeletonHeaders
            ? columns.map((c) => (typeof c.title === 'function' ? null : c.title))
            : undefined
        }
      />
    );
  }

  if (errorText && query.isError && !query.isFetching) {
    return <RegionError message={errorText} onRetry={() => void query.refetch()} />;
  }

  const showToolbar = toolbar !== undefined || !hideReload;

  return (
    <div className={clsx(styles.wrap, className)}>
      {showToolbar && (
        <div className={styles.toolbar}>
          <div>{toolbar}</div>
          {!hideReload && (
            <div className={styles.right}>
              <Button
                icon={<ArrowsClockwiseIcon size={16} />}
                onClick={() => query.refetch()}
                aria-label="Reload"
              />
            </div>
          )}
        </div>
      )}

      {bulkActions && selectedRows.length > 0 ? (
        <div className={styles.bulkBar}>
          <div>{selectedRows.length} selected</div>
          <Space>
            {bulkActions.map((a) => (
              <Button
                key={a.key}
                disabled={a.disabled?.(selectedRows)}
                onClick={() => a.onRun(selectedRows, clearSelection)}
              >
                {a.label}
              </Button>
            ))}
            <Button type="text" onClick={clearSelection}>
              Clear
            </Button>
          </Space>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        <Table<TRow>
          className={styles.table}
          rowKey={rowKey as never}
          tableLayout={hasExplicitWidth ? 'fixed' : undefined}
          columns={themedColumns}
          dataSource={query.data?.data ?? []}
          loading={query.isFetching}
          pagination={false}
          locale={
            loadErrorText !== undefined && query.isError && !query.data
              ? { emptyText: loadErrorText }
              : emptyText !== undefined
                ? { emptyText }
                : undefined
          }
          rowClassName={rowClassName ? (row) => rowClassName(row) ?? '' : undefined}
          onChange={onChange}
          onRow={
            onRowClick
              ? (record) =>
                  isRowClickable && !isRowClickable(record)
                    ? { 'data-dt-inert': '' }
                    : {
                        onClick: (e: MouseEvent<HTMLElement>) => {
                          const target = e.target as HTMLElement;
                          // antd portals dropdown/overlay content to document.body; React still
                          // bubbles the synthetic event through the React tree across that portal
                          // boundary, so a menu-item click reaches here even though it's nowhere
                          // near this row in the real DOM. Ignore anything not actually inside the
                          // row's own DOM subtree (covers portaled action-menu items), then ignore
                          // the action cell itself (covers the ellipsis trigger button).
                          if (!e.currentTarget.contains(target)) return;
                          if (target.closest('[data-dt-action-cell]')) return;
                          onRowClick(record);
                        },
                        style: { cursor: 'pointer' },
                      }
              : undefined
          }
          scroll={hasFixedColumn ? { x: totalWidth ?? 'max-content' } : undefined}
          rowSelection={
            bulkActions
              ? {
                  selectedRowKeys: selectedKeys,
                  onChange: (keys, rows) => {
                    setSelectedKeys(keys);
                    setSelectedRows(rows);
                  },
                }
              : undefined
          }
        />
      </div>

      {hidePagination ? null : (
        <div className={styles.pagination}>
          <PaginationFooter
            page={page}
            pageSize={pageSize}
            total={query.data?.total ?? 0}
            pageSizeOptions={pageSizeOptions}
            onChange={(nextPage, nextPageSize) =>
              setState({ page: nextPage, pageSize: nextPageSize })
            }
          />
        </div>
      )}
    </div>
  );
}
